import React, { useState, useEffect, useMemo } from 'react';
import SearchField from '../SearchField'
import api from '../../api/client';
import { toast } from '../../api/toast';
import Pagination from '../common/Pagination'
import RowActions from '../common/RowActions'
import { beginImpersonation } from '../../api/impersonation';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  CircularProgress,
  Typography,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  TextField,
  InputAdornment,
  makeStyles,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
} from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit'
import CloseIcon from '@material-ui/icons/Close';
import DeleteIcon from '@material-ui/icons/Delete';
import SearchIcon from '@material-ui/icons/Search';
import AddIcon from '@material-ui/icons/Add';
import { logoSrc } from '../../api/logoSrc';
import { toFarsiDigits } from '../../assets/utils';
import { exportExcel } from '../../assets/excelUtils';
import { useRecoilState } from 'recoil';
import { userInfoState } from '../../recoil/atoms';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import SendIcon from '@material-ui/icons/Send';

// 9Router (1405/08/11): شمارهٔ موبایل با پوشش، برای نمایش در تأییدیهٔ ارسال کد
const maskMobile = (m) => {
  const s = String(m || '').replace(/\D/g, '');
  if (!s) return '-';
  return s.length >= 8 ? `${s.slice(0, 4)}***${s.slice(-3)}` : `${s.slice(0, 2)}***`;
};

const useStyles = makeStyles((theme) => ({
  root: {
    background: '#f1f1f1',
    flexGrow: 1,
    marginTop: theme.spacing(3),
  },
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    direction: 'rtl',
  },
  searchBox: {
    minWidth: 280,
    maxWidth: 400,
  },
  filterControl: {
    minWidth: 160,
  },
  actions: {
    display: 'flex',
    gap: theme.spacing(1),
  },
  loginBtn: {
    borderRadius: 6,
    padding: '4px 14px',
    fontSize: 12,
    fontWeight: 'bold',
    textTransform: 'none',
    backgroundColor: '#1a237e',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#283593',
    },
  },
  // جدول فشرده ⇒ با حذفِ ستون‌های خالی، معمولاً بدونِ اسکرولِ افقی جا می‌شود
  table: {
    fontSize: 12,
    '& th, & td': {
      padding: '7px 9px',
      whiteSpace: 'nowrap',
    },
  },
}));

const roleMap = {
  Admin: 'ادمین',
  Department: 'اداره',
  Contractor: 'پیمانکار',
  Consultant: 'مشاور',
  Supervisor: 'ناظر',
  DepartmentManager: 'مدیر سازمان',
};

// رنگ چیپ هر نقش
const roleStyle = {
  Admin: { background: '#e8eafd', color: '#1a237e' },
  Department: { background: '#e3f2fd', color: '#1565c0' },
  Contractor: { background: '#f3e5f5', color: '#7b1fa2' },
  Consultant: { background: '#fff3e0', color: '#ef6c00' },
  Supervisor: { background: '#e0f7fa', color: '#00838f' },
  DepartmentManager: { background: '#e0f2f1', color: '#00796b' },
};

// رنگِ نامِ پیمانکار/مشاور/ناظر/اداره/مدیر سازمان (برای تفکیکِ بصریِ نقش)
const NAME_ROLE_STYLE = {
  Contractor: { bg: '#f3e5f5', fg: '#7b1fa2' },   // بنفش
  Consultant: { bg: '#fff3e0', fg: '#ef6c00' },   // نارنجی
  Supervisor: { bg: '#e0f7fa', fg: '#00838f' },   // فیروزه‌ای
  Department: { bg: '#e3f2fd', fg: '#1565c0' },   // آبی
  DepartmentManager: { bg: '#e0f2f1', fg: '#00796b' }, // سبز-آبی (مدیر سازمان)
};

/** نقشی که نامِ نمایش‌داده‌شده به آن تعلق دارد */
const nameRoleOf = (user) => {
  if (!user) return '';
  if (user.role === 'DepartmentManager' || user.manager_name) return 'DepartmentManager';
  if (user.supervisor_name) return 'Supervisor';
  if (user.contractor_name) return 'Contractor';
  if (user.consultant_name) return 'Consultant';
  if (user.department_name) return 'Department';
  return '';
};

/** نامِ نمایشیِ شخص/سازمان متناسب با نقش */
const personNameOf = (user) => {
  if (!user) return '';
  switch (nameRoleOf(user)) {
    case 'DepartmentManager': return user.manager_name || user.username;
    case 'Supervisor': return user.supervisor_name;
    case 'Contractor': return user.contractor_name;
    case 'Consultant': return user.consultant_name;
    case 'Department': return user.department_name;
    default: return '';
  }
};

/**
 * ستون‌های جدولِ کاربران — داده‌محور، تا بتوان ستون‌هایِ بی‌محتوا را
 * از هر صفحه حذف کرد (تا جدول نیازی به اسکرولِ افقی نداشته باشد).
 */
const COLUMNS = [
  {
    key: 'logo', label: 'لوگو', align: 'center',
    has: (u) => !!u.logo,
    cell: (u) => (u.logo ? (
      <img
        src={logoSrc(u.logo)}
        alt=""
        style={{ width: 34, height: 34, borderRadius: '50%', objectFit: 'cover', border: '1.5px solid #e3e6f2' }}
        onError={(e) => { e.target.style.display = 'none' }}
      />
    ) : <span style={{ color: '#bbb' }}>—</span>),
  },
  {
    key: 'name', label: 'پیمانکار/مشاور/ناظر', align: 'right', right: true,
    has: (u) => !!nameRoleOf(u),
    cell: (u) => (nameRoleOf(u) ? (
      <span style={{
        fontWeight: 800,
        color: NAME_ROLE_STYLE[nameRoleOf(u)].fg,
        background: NAME_ROLE_STYLE[nameRoleOf(u)].bg,
        borderRadius: 999,
        padding: '3px 12px',
        display: 'inline-block',
      }}>
        {personNameOf(u)}
      </span>
    ) : '-'),
  },
  {
    key: 'contracts', label: 'قرارداد', align: 'center',
    has: (u) => !!u.contractor_id,
    cell: (u) => (u.contractor_id ? (
      <span style={{ fontWeight: 700, background: '#e8eaf6', color: '#283593', borderRadius: 8, padding: '3px 10px' }}>{toFarsiDigits(u.contracts_count ?? 0)}</span>
    ) : '-'),
  },
  {
    key: 'supervised', label: 'تحت نظارت', align: 'center',
    has: (u) => !!u.supervised_count,
    cell: (u) => (u.supervised_count ? (
      <span style={{ fontWeight: 700, background: '#e0f7fa', color: '#00695c', borderRadius: 8, padding: '3px 10px' }}>{toFarsiDigits(u.supervised_count)}</span>
    ) : '-'),
  },
  {
    key: 'consulted', label: 'تحت مشاوره', align: 'center',
    has: (u) => !!u.consulted_count,
    cell: (u) => (u.consulted_count ? (
      <span style={{ fontWeight: 700, background: '#fff3e0', color: '#e65100', borderRadius: 8, padding: '3px 10px' }}>{toFarsiDigits(u.consulted_count)}</span>
    ) : '-'),
  },
  {
    key: 'transfers', label: 'حواله در یکسال گذشته', align: 'center',
    has: (u) => !!u.contractor_id,
    cell: (u) => (u.contractor_id ? (
      <span style={{ fontWeight: 700, background: '#e0f2f1', color: '#00695c', borderRadius: 8, padding: '3px 10px' }}>{toFarsiDigits(u.transfers_year ?? 0)}</span>
    ) : '-'),
  },
  {
    key: 'departments', label: 'اداره/سازمان', align: 'right', right: true, wrap: true,
    has: (u) => !!(u.related_departments || u.department_name),
    cell: (u) => (u.related_departments || u.department_name || '-'),
  },
  {
    key: 'subscribed', label: 'اشتراک', align: 'center',
    has: (u) => u.is_subscribed === true || u.is_subscribed === false,
    cell: (u) => (u.is_subscribed === true ? (
      <span style={{
        fontSize: 11.5, fontWeight: 800, color: '#8a6100',
        background: 'linear-gradient(135deg, #fff3c4 0%, #ffe082 100%)',
        border: '1px solid #e6b800', borderRadius: 999, padding: '3px 10px', whiteSpace: 'nowrap',
      }}>★ مشترک</span>
    ) : u.is_subscribed === false ? (
      <span style={{ color: '#9aa1b9', fontSize: 12 }}>✗</span>
    ) : '-'),
  },
  {
    key: 'role2', label: 'نقش دوم', align: 'center',
    has: (u) => !!u.role2,
    cell: (u) => (u.role2 ? (
      <Chip
        label={roleMap[u.role2] || u.role2}
        size="small"
        style={{ fontWeight: 700, ...(roleStyle[u.role2] || { background: '#eceff1', color: '#455a64' }) }}
      />
    ) : '-'),
  },
  {
    key: 'mobile', label: 'موبایل', align: 'center', ltr: true,
    has: (u) => !!u.mobile,
    cell: (u) => (u.mobile ? toFarsiDigits(`0${u.mobile}`) : '-'),
  },
  {
    key: 'last_login', label: 'آخرین ورود', align: 'center',
    has: (u) => !!u.last_login,
    cell: (u) => (u.last_login ? (
      <span
        onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('arjan-user-visits', { detail: u })) }}
        title="نمایش تاریخچهٔ بازدید"
        style={{
          fontSize: 11.5, background: '#f3e5f5', color: '#7b1fa2', borderRadius: 8,
          padding: '3px 8px', fontWeight: 700, whiteSpace: 'nowrap',
          cursor: 'pointer', textDecoration: 'underline dotted',
        }}>{formatDate(u.last_login)}</span>
    ) : (
      <span onClick={(e) => { e.stopPropagation(); window.dispatchEvent(new CustomEvent('arjan-user-visits', { detail: u })) }}
        title="نمایش تاریخچهٔ بازدید"
        style={{ color: '#9aa1b9', cursor: 'pointer' }}>— بازدیدها</span>
    )),
  },
]

const thStyle = {
  backgroundColor: '#1a237e',
  color: '#fff',
  fontWeight: 700,
  fontSize: 12.5,
  position: 'sticky',
  top: 0,
  zIndex: 2,
  whiteSpace: 'nowrap',
  borderLeft: '1px solid rgba(255,255,255,.15)',
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('fa-IR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return '-';
  }
};

const UsersList = ({ setOpenEditUser, setOpenDeleteUser, setOpenAddUser }) => {
  const [visOpen, setVisOpen] = useState(false);
  useEffect(() => {
    const onAsk = (ev) => { if (ev && ev.detail) openVisits(ev.detail); };
    window.addEventListener('arjan-user-visits', onAsk);
    return () => window.removeEventListener('arjan-user-visits', onAsk);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [visData, setVisData] = useState(null);
  const [visLoading, setVisLoading] = useState(false);
  const openVisits = async (u) => {
    setVisOpen(true); setVisData(null); setVisLoading(true);
    try {
      const r = await api.get(`users/admin/users/${u.id}/visits/`);
      setVisData(r.data);
    } catch (e) { setVisData(null); }
    finally { setVisLoading(false); }
  };
  const classes = useStyles();
  const [userInfo, setUserInfo] = useRecoilState(userInfoState);

  // ورود ادمین با نقش کاربر — همان کلید جدول سازمانها؛ by=user یعنی شناسه = User.id
  const loginHandler = (id) => {
    // ذخیرهٔ نشست ادمین قبل از ورود جایگزین، تا «خروج» بعدی به همان صفحه برگرداند
    beginImpersonation(userInfo);
    api.post('users/login/' + id + '?by=user').then((result) => {
      if (result.status === 200) {
        setUserInfo(result.data.user);
        localStorage.setItem('access', result.data.tokens.access);
        // refresh هم باید توکنِ همین کاربر شود، وگرنه رفرشِ بعدی نشست را به ادمین برمی‌گرداند
        if (result.data.tokens.refresh) {
          localStorage.setItem('refresh', result.data.tokens.refresh);
        }
        api.defaults.headers.common['Authorization'] = 'Bearer ' + result.data.tokens.access;
      }
    });
  };

  const [users, setUsers] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [loading, setLoading] = useState(false);
  // 9Router (1405/08/11): ارسال کد ورود (دعوت) از طرف ادمین — فقط برای «مدیر سازمان»
  const [otpUser, setOtpUser] = useState(null);
  const [otpBusy, setOtpBusy] = useState(false);
  const [otpMsg, setOtpMsg] = useState(null);

  const sendOtp = async () => {
    if (!otpUser) return;
    setOtpBusy(true);
    setOtpMsg(null);
    try {
      const res = await api.post(`users/admin/users/${otpUser.id}/send-invite/`);
      const msg = (res.data && res.data.message) || 'پیامک دعوت ارسال شد.';
      toast(msg);
      setOtpUser(null);            // بستنِ کارت در صورت موفقیت
    } catch (e) {
      const d = (e && e.response && e.response.data) || {};
      const wait = d.retry_after ? ` (${toFarsiDigits(d.retry_after)} ثانیهٔ دیگر تلاش کنید)` : '';
      setOtpMsg({ ok: false, text: `${d.detail || 'ارسال ناموفق بود.'}${wait}` });
    } finally {
      setOtpBusy(false);
    }
  };

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line
  }, [page, rowsPerPage, search, roleFilter]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = {
        page: page + 1,
        count: rowsPerPage,
      };
      if (search) {
        params.search = search;
      }
      if (roleFilter) {
        params.role = roleFilter;
      }
      const response = await api.get('users/admin/users/', { params });
      
      if (response.data && response.data.users) {
        setUsers(response.data.users);
        setTotalCount(response.data.summary?.count || 0);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // ستون‌هایی که در این صفحه هیچ داده‌ای ندارند ⇒ حذف می‌شوند (نیازی به اسکرول افقی نیست)
  const visibleCols = useMemo(
    () => COLUMNS.filter((c) => users.some((u) => c.has(u))),
    [users]
  );
  // ۳ ستونِ ثابت (ردیف، نام کاربری، نقش) + ستون‌های داده‌محور + ستونِ عملیات
  const colCount = visibleCols.length + 4;

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // جستجوی زنده هنگام تایپ — بدون دکمهٔ جستجو (لوکل تایپ + دلی ۴۰۰ms)
  useEffect(() => {
    const q1 = searchInput.trim() === search.trim() ? 0 : 400
    const t = setTimeout(() => {
      setSearch(searchInput)
      setPage(0)
    }, q1)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput])

  return (
    <Card elevation={5} className={classes.root} data-tour="admin-users">
      <CardHeader
        title="کاربران"
        action={
          <Box style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            {users.length > 0 && (
              <Button
                variant="contained"
                color="secondary"
                onClick={() => exportExcel({
                  filename: 'users.xlsx',
                  title: 'گزارش کاربران' + (search ? ` — فیلتر «${search}»` : '') + (roleFilter ? ` — نقش ${roleFilter}` : ''),
                  headers: ['ردیف', 'نام کاربری', 'نقش', 'نقش دوم', 'موبایل', 'پیمانکار/مشاور/ناظر', 'قرارداد', 'تحت نظارت', 'تحت مشاوره', 'حواله در یکسال گذشته', 'اداره/سازمان', 'اشتراک', 'آخرین ورود'],
                  rows: users.map((user, index) => [
                    page * rowsPerPage + index + 1,
                    user.username || '-',
                    roleMap[user.role] || user.role || '-',
                    user.role2 ? (roleMap[user.role2] || user.role2) : '-',
                    user.mobile ? '0' + user.mobile : '-',
                    user.contractor_name || user.consultant_name || user.supervisor_name || user.manager_name || '-',
                    user.contractor_id ? (user.contracts_count ?? 0) : '-',
                    user.supervised_count || '-',
                    user.consulted_count || '-',
                    user.contractor_id ? (user.transfers_year ?? 0) : '-',
                    user.related_departments || user.department_name || '-',
                    user.is_subscribed === true ? 'بله (مشترک)' : user.is_subscribed === false ? 'خیر' : '-',
                    user.last_login ? formatDate(user.last_login) : '-',
                  ]),
                })}
              >
                دانلود اکسل
              </Button>
            )}
            {setOpenAddUser && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => setOpenAddUser(true)}
              >
                افزودن کاربر
              </Button>
            )}
          </Box>
        }
      />
      <Divider />
      <CardContent>
        <Box className={classes.toolbar}>
          <SearchField
            className={classes.searchBox}
            placeholder="جستجو: نام، نام کاربری، موبایل، کد ملی (پیمانکار/سازمان/ناظر/مشاور)"
            variant="outlined"
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          <FormControl variant="outlined" size="small" className={classes.filterControl}>
            <InputLabel>فیلتر نقش</InputLabel>
            <Select
              value={roleFilter}
              onChange={(e) => {
                setRoleFilter(e.target.value);
                setPage(0);
              }}
              label="فیلتر نقش"
            >
              <MenuItem value="">همه نقش‌ها</MenuItem>
              {Object.entries(roleMap).map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {(search || roleFilter) && (
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setRoleFilter('');
                setPage(0);
              }}
            >
              پاک کردن فیلتر
            </Button>
          )}
        </Box>

        <TableContainer elevation={5} component={Paper} style={{ overflowX: 'auto', }}>
          <Table size="small" className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell align="center" style={thStyle}>ردیف</TableCell>
                <TableCell align="right" style={{ ...thStyle, textAlign: 'right' }}>نام کاربری</TableCell>
                <TableCell align="center" style={thStyle}>نقش</TableCell>
                {visibleCols.map((c) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    style={c.right ? { ...thStyle, textAlign: 'right' } : thStyle}
                  >
                    {c.label}
                  </TableCell>
                ))}
                <TableCell align="center" style={thStyle}>عملیات</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    در حال بارگذاری...
                  </TableCell>
                </TableRow>
              )}
              {!loading && users.length === 0 && (
                <TableRow>
                  <TableCell colSpan={colCount} align="center">
                    کاربری یافت نشد
                  </TableCell>
                </TableRow>
              )}
              {!loading &&
                users.map((user, index) => (
                  <TableRow key={user.id}>
                    <TableCell align="center">
                      {toFarsiDigits(page * rowsPerPage + index + 1)}
                    </TableCell>
                    <TableCell align="right" style={{ textAlign: 'right' }}>
                      {toFarsiDigits(user.username || '-')}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={roleMap[user.role] || user.role || '-'}
                        size="small"
                        style={{
                          fontWeight: 700,
                          ...(roleStyle[user.role] || { background: '#eceff1', color: '#455a64' }),
                        }}
                      />
                    </TableCell>
                    {visibleCols.map((c) => (
                      <TableCell
                        key={c.key}
                        align={c.align}
                        dir={c.ltr ? 'ltr' : undefined}
                        style={c.right
                          ? { whiteSpace: c.wrap ? 'pre-line' : 'nowrap', textAlign: 'right', maxWidth: 220 }
                          : undefined}
                      >
                        {c.cell(user)}
                      </TableCell>
                    ))}
                    <TableCell align="center">
                      <div className={classes.actions}>
                        <Button
                          size="small"
                          className={classes.loginBtn}
                          startIcon={<ExitToAppIcon style={{ fontSize: 16 }} />}
                          onClick={() => loginHandler(user.id)}
                        >
                          ورود
                        </Button>
                        {nameRoleOf(user) === 'DepartmentManager' && (
                          <Button size="small" title="ارسال پیامک دعوت (متنِ زمانِ ساختِ حساب)"
                            onClick={() => { setOtpMsg(null); setOtpUser(user); }}>
                            <SendIcon htmlColor="#00796b" />
                          </Button>
                        )}
                        <Button size="small" onClick={() => setOpenEditUser(user.id)}>
                          <EditIcon htmlColor="orangered" />
                        </Button>
                        <Button size="small" onClick={() => setOpenDeleteUser(user.id)}>
                          <DeleteIcon htmlColor="orangered" />
                        </Button>
                        <RowActions userId={user.id} mobile={user.mobile}
                          name={user.name || user.username} />
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && users.length > 0 && (
                  <TableRow>
                    <TableCell colSpan={16} align="center" style={{
                      background: '#f5f6fb',
                      color: '#7b84b8',
                      fontWeight: 700,
                      fontSize: 13,
                      padding: 10,
                      borderTop: '2px dashed #d5daf3',
                    }}>
                      ✓ پایان این صفحه — {toFarsiDigits(users.length)} مورد از {toFarsiDigits(totalCount)}
                    </TableCell>
                  </TableRow>
                )}
            </TableBody>
          </Table>
        </TableContainer>

        <Pagination
          page={page + 1}
          pages={Math.ceil((totalCount || 0) / (rowsPerPage || 20))}
          onChange={(p) => handleChangePage(null, p - 1)}
          extra={(
            <select
              value={rowsPerPage}
              onChange={handleChangeRowsPerPage}
              style={{
                height: 30, borderRadius: 8, border: '1px solid #c5cae9',
                background: '#fff', color: '#3949ab', fontSize: 12.5, padding: '0 6px',
              }}
            >
              {[10, 20, 50, 100].map((n) => (
                <option key={n} value={n}>{toFarsiDigits(n)} در صفحه</option>
              ))}
            </select>
          )}
        />
      </CardContent>

      <Dialog open={visOpen} onClose={() => setVisOpen(false)} maxWidth="sm" fullWidth dir="rtl">
        <DialogTitle style={{ fontWeight: 800 }}>
          تاریخچهٔ بازدید {visData && visData.user && visData.user.name ? `— ${visData.user.name}` : ''}
          <IconButton size="small" style={{ float: 'left' }} onClick={() => setVisOpen(false)}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {visLoading && <Box textAlign="center" py={3}><CircularProgress size={24} /></Box>}
          {!visLoading && !visData && (
            <Typography style={{ color: '#c62828', fontWeight: 700 }}>
              دریافت تاریخچه ناموفق بود.
            </Typography>
          )}
          {!visLoading && visData && (
            <>
              <Box display="flex" gap={8} flexWrap="wrap" style={{ marginBottom: 12, fontSize: 12.5 }}>
                <Chip size="small" label={`${toFarsiDigits(visData.total_sessions)} نشست`}
                  style={{ background: '#e8eaf6', color: '#283593', fontWeight: 700 }} />
                <Chip size="small" label={`${toFarsiDigits(visData.total_days)} روزِ بازدید`}
                  style={{ background: '#e0f2f1', color: '#00695c', fontWeight: 700 }} />
                <span style={{ color: '#607d8b' }}>آخرین ورود: {visData.user.last_login || '—'}</span>
              </Box>

              {(visData.sessions || []).length === 0 && (
                <Typography variant="body2" color="textSecondary">نشستی ثبت نشده است.</Typography>
              )}
              {(visData.sessions || []).map((s) => (
                <Box key={s.id} display="flex" justifyContent="space-between" alignItems="center"
                  style={{ padding: '6px 0', borderBottom: '1px solid #f0f1f8', fontSize: 12.5 }}>
                  <span style={{ fontWeight: 700, color: '#1a237e' }}>{s.date_fa}</span>
                  <span>ورود: {s.start}</span>
                  <span>خروج: {s.end}</span>
                  <span style={{ color: '#00695c', fontWeight: 700 }}>
                    {s.minutes != null ? `${toFarsiDigits(s.minutes)} دقیقه` : '—'}
                  </span>
                  <span dir="ltr" style={{ color: '#78909c', fontSize: 11 }}>{s.ip}</span>
                </Box>
              ))}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* 9Router (1405/08/11): تأییدیهٔ ارسال کد ورود به مدیر سازمان */}
      <Dialog open={!!otpUser} onClose={() => { if (!otpBusy) setOtpUser(null); }} maxWidth="xs" fullWidth dir="rtl">
        <DialogTitle style={{ fontWeight: 800 }}>
          ارسال پیامک دعوت
          <IconButton size="small" style={{ float: 'left' }} onClick={() => { if (!otpBusy) setOtpUser(null); }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography style={{ fontSize: 13, lineHeight: 2 }}>
            برای <b>{otpUser ? (otpUser.name || otpUser.username) : ''}</b> متنِ دعوتِ حساب (همان پیامکِ
            زمانِ ساختِ حساب) به شمارهٔ{' '}
            <span dir="ltr">{maskMobile(otpUser && otpUser.mobile)}</span> ارسال شود؟
          </Typography>
          {otpMsg && (
            <Typography style={{ marginTop: 10, fontSize: 12.5, fontWeight: 700, color: otpMsg.ok ? '#00695c' : '#c62828' }}>
              {otpMsg.text}
            </Typography>
          )}
          <Box display="flex" justifyContent="flex-end" style={{ marginTop: 16, gap: 8 }}>
            <Button onClick={() => setOtpUser(null)} disabled={otpBusy}>انصراف</Button>
            <Button variant="contained" color="primary" onClick={sendOtp} disabled={otpBusy}>
              {otpBusy ? <CircularProgress size={18} color="inherit" /> : 'ارسال دعوت'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default UsersList;
